import { gsap } from "gsap";
import * as PIXI from "pixi.js";
import { Dictionary } from "../../util/collection/Dictionary";
import { Logger } from "../../util/log/Logger";
import { ObjectUtil } from "../../util/object/ObjectUtil";
import { StringUtil } from "../../util/string/StringUtil";
import { DestroyUtil } from "../../util/view/DestroyUtil";
import { FontUtil } from "../../util/view/FontUtil";
import { GraphicsUtil } from "../../util/view/GraphicsUtil";
import { LayoutUtil } from "../../util/view/LayoutUtil";
import { WebUtil } from "../../util/web/WebUtil";
import { Renderer, ResizeEvent } from "../renderer/Renderer";
import { OrientationType } from "../renderer/constant/RendererConstants";
import { AbstractControl } from "./AbstractControl";
import { TitanSprite } from "./TitanSprite";
import { ControlConstants } from "./constant/ControlConstants";
import { Font, FontProperties } from "./properties/FontProperties";
/**
 * TitanText.ts
 * Text that auto-resizes to its container
 */
export class TitanText extends AbstractControl {
    /**
     * Enable and Show field.
     */
    static showField(field) {
        if (field) {
            this.enableField(field);
            field.show();
        }
    }
    /**
     * Disable and Hide field.
     */
    static hideField(field) {
        if (field) {
            this.disableField(field);
            field.hide();
        }
    }
    /**
     * @param field - Text field to disable.
     * @param alpha - Set `field` alpha to this. Default `0.5`.
     */
    static disableField(field, alpha = 0.5) {
        if (field) {
            field.alpha = alpha;
        }
    }
    /**
     * Set `field` alpha to 1.0.
     * @param field - Text field to enable.
     */
    static enableField(field) {
        if (field) {
            field.alpha = 1;
        }
    }
    static makeTextStyle(properties, fontProperties) {
        let fProps;
        if (fontProperties) {
            fProps = fontProperties;
        }
        else if (properties.fontProperties) {
            fProps = properties.fontProperties;
        }
        else {
            fProps = new FontProperties(new Font(TitanText.defaultFontName), TitanText.defaultFontSize, TitanText.defaultFontColor);
        }
        const style = FontUtil.createPixiTextStyle(fProps);
        style["align"] = properties.horizontalAlign || "center";
        let size = properties.size;
        if (size && properties.multiline) {
            style["wordWrap"] = true;
            style["wordWrapWidth"] = size.x;
        }
        return style;
    }
    /**
     * Helper function to collect all font styles that have had their text auto-resized along with their final font size.
     * Useful when you want to avoid resizeFont() calls. Warning: function is recursive
     * @param container - Starting point for field collection. Will traverse all branches in the scene graph looking for text fields.
     * @returns Dictionary containing the font style key and the final fontSize of the field.
     */
    static getAdjustedFontSizes(container) {
        const returnVal = new Dictionary();
        const _getAdjustedFontSizes = (container) => {
            if (container instanceof TitanText) {
                const properties = container.getProperties();
                if (properties._resizes > 0) {
                    returnVal.setValue(properties.styleId, container.getPixiText().style.fontSize);
                }
            }
            container.children.forEach((child) => {
                if (child instanceof PIXI.Container) {
                    _getAdjustedFontSizes(child);
                }
            });
        };
        _getAdjustedFontSizes(container);
        return returnVal;
    }
    /**
     * Helper function to collect all textFields that haven't got a size setting. Warning: function is recursive
     * @param container - Starting point for field collection. Will traverse all branches in the scene graph looking for text fields.
     * @returns Array of TextFields that are missing a "size" property.
     */
    static getUnsizedFields(container) {
        const returnVal = [];
        const _getUnsizedFields = (container) => {
            if (container instanceof TitanText) {
                const properties = container.getProperties();
                if (!properties.size ||
                    properties.size.x <= 0 ||
                    properties.size.y <= 0) {
                    returnVal.push(container);
                }
            }
            container.children.forEach((child) => {
                if (child instanceof PIXI.Container) {
                    _getUnsizedFields(child);
                }
            });
        };
        _getUnsizedFields(container);
        return returnVal;
    }
    render(renderer) {
        if (this.visible) {
            this.initialiseUiText();
            if (this.renderer.isPointerOverDisplayObject(this)) {
                this.populateAriaDiv();
            }
        }
        else {
            this.deinitialiseUiText();
        }
        super.render(renderer);
    }
    create() {
        super.create();
        this.createLocale();
        this.createHolder();
        this.createTextFields();
        this.updateRotation();
    }
    createAccessibility() {
        //no-op - handled in render function
    }
    populateAriaDiv() {
        const ariaDiv = Renderer.getInstance().getScreenReaderRegion();
        const label = this.uiProperties.accessibleOptions?.accessibleTitle ?? `${this.getText()}`;
        ariaDiv.textContent = label;
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerCore(ResizeEvent.DEINITIALISE_TEXT, this.handleDeinitialiseText);
        this.registerCore(ResizeEvent.REINITIALISE_TEXT, this.handleReinitialiseText);
    }
    removeEventListeners() {
        super.removeEventListeners();
        this.unregisterCore(ResizeEvent.DEINITIALISE_TEXT);
        this.unregisterCore(ResizeEvent.REINITIALISE_TEXT);
    }
    destroy() {
        super.destroy();
        DestroyUtil.destroy(this.holder);
        DestroyUtil.destroy(this.uiText);
        this.landscapeDefaultTextStyle = null;
        this.portraitDefaultTextStyle = null;
        if (this.isLocalise || this.isGameDev)
            DestroyUtil.destroy(this.bounds);
        this.holder = null;
        this.uiText = null;
        this.bounds = null;
        this.originalText = null;
        this.formattedText = null;
        this.isLocalise = null;
        this.isGameDev = null;
        this.locale = null;
        this.uiProperties = null;
    }
    deinitialiseUiText() {
        if (this._initialised) {
            gsap.killTweensOf(this.uiText);
            this.uiText.destroy({ children: true });
            this.uiText = null;
            this._initialised = false;
            this.holder.renderable = false;
        }
    }
    async initialiseUiText() {
        if (!this._initialised) {
            if (!this._allowInitialise) {
                await gsap.delayedCall(0.1, () => this.initialiseUiText());
                return;
            }
            this._initialised = true;
            if (!this.uiText && this.uiProperties) {
                let style;
                if (this.renderer.isPortrait() && this.hasPortraitProperties) {
                    style = this._portraitDirty
                        ? this.portraitDefaultTextStyle.clone()
                        : this.portraitTextStyle;
                }
                else {
                    style = this._landscapeDirty
                        ? this.landscapeDefaultTextStyle.clone()
                        : this.landscapeTextStyle;
                }
                this.uiText = this.createPixiText(style);
                if (this.renderer.isLandscape() ||
                    !this.hasPortraitProperties) {
                    this.landscapeTextStyle = this.uiText.style.clone();
                }
                else {
                    this.portraitTextStyle = this.uiText.style.clone();
                }
                this.uiText.text = this.formattedText;
                this.createAccessibility();
            }
            this.createBounds();
            if (this.parent) {
                this.updateTransform();
            }
        }
    }
    updateBounds() {
        if (this.renderer.isResizing() || !this.uiText) {
            return;
        }
        const size = this.getSize();
        if (size && this.uiText) {
            this.uiText.updateText(false);
            this.uiText.updateTransform();
            if (this.uiText.style.wordWrap) {
                this.uiText.style.wordWrapWidth = Math.min(this.getDefaultTextStyle().wordWrapWidth, size.x);
            }
            const landscape = !this.hasPortraitProperties
                ? true
                : this.renderer.isLandscape();
            const clean = !(landscape
                ? this._landscapeDirty
                : this._portraitDirty);
            if (clean ||
                !this.uiProperties.autoResize ||
                this.isLocalise ||
                this.uiText.style.fontSize === 1 ||
                this.checkBounds(size.x, size.y, false)) {
                this.updateHorizontalAlign();
                this.updateVerticalAlign(size);
                if (landscape) {
                    this._landscapeDirty = false;
                    this.landscapeTextStyle = this.uiText.style.clone();
                }
                else {
                    this._portraitDirty = false;
                    this.portraitTextStyle = this.uiText.style.clone();
                }
                this.holder.renderable = true;
                //console.warn(this.uiText.text + ":  " + this.uiProperties._resizes);
            }
            else if (this.uiText.text && this.uiText.text.length) {
                this.resizeFont();
                this.updateBounds();
            }
        }
    }
    updateNoBounds() {
        if (this.renderer.isResizing() || !this.uiText) {
            return;
        }
        this.uiText.updateText(false);
        this.uiText.updateTransform();
        this.holder.renderable = true;
    }
    getPixiText() {
        this.initialiseUiText();
        return this.uiText;
    }
    getSize() {
        let size = this.uiProperties.size;
        if (!size && this.isLocalise) {
            size = new PIXI.Point(this.width, this.height);
        }
        return size;
    }
    getMetricsData(respectDirty = true) {
        if ((!respectDirty && !this.renderer.isResizing()) ||
            !this.uiProperties._metrics) {
            this.uiProperties._metrics = PIXI.TextMetrics.measureText(this.uiText.text, this.uiText.style, this.uiText.style.wordWrap);
        }
        return {
            width: this.uiProperties._metrics.width,
            height: this.uiProperties._metrics.height,
            lines: this.uiProperties._metrics.lines.length,
            lineHeight: this.uiProperties._metrics.lineHeight,
            ascent: this.uiProperties._metrics.fontProperties.ascent,
            descent: this.uiProperties._metrics.fontProperties.descent
        };
    }
    drawBounds(width, height, exists) {
        if (!this._initialised) {
            return;
        }
        //Set bounds based on alignment
        let xx;
        let yy;
        let ww = width > 0 ? width : this.uiText.width;
        let hh = height > 0 ? height : this.uiText.height;
        switch (this.uiProperties.horizontalAlign) {
            case ControlConstants.CENTER:
                xx = -ww / 2;
                break;
            case ControlConstants.RIGHT:
                xx = -ww;
                break;
            case ControlConstants.LEFT:
            default:
                xx = 0;
                break;
        }
        switch (this.uiProperties.verticalAlign) {
            case ControlConstants.CENTER:
                yy = -hh / 2;
                break;
            case ControlConstants.BOTTOM:
                yy = -hh;
                break;
            case ControlConstants.TOP:
            default:
                yy = 0;
                break;
        }
        if (this.isLocalise || this.isGameDev) {
            if (this.bounds instanceof PIXI.Graphics) {
                this.bounds.clear();
            }
            else {
                this.bounds = new PIXI.Graphics();
            }
            // draw bounds
            let valid = exists && this.checkBounds(ww, hh, false);
            let colour = valid ? 0x00ff00 : 0xff0000;
            this.bounds.beginFill(colour, 1).drawRect(xx, yy, ww, hh).endFill();
            this.holder.addChildAt(this.bounds, 0);
            if (!this.isLocalise && this.isGameDev) {
                this.bounds.visible = false;
                this.bounds.alpha = 0.2;
            }
        }
        else {
            this.bounds = new PIXI.Rectangle(xx, yy, ww, hh);
        }
    }
    clear() {
        this.updateTextField("");
    }
    /**
     * @param value - Formatted text
     * @param refreshStyle - Will the text be repositioned and resized to fit within its bounds. Default `false`.
     */
    setText(value, refreshStyle = false) {
        if (refreshStyle && this.uiText) {
            ObjectUtil.overwriteProperties(this.uiText.style, this.getDefaultTextStyle(), this.getDefaultTextStyle());
            this._landscapeDirty = true;
            this._portraitDirty = true;
        }
        this.updateTextField(value);
        if (this.isLocalise) {
            this.refreshBounds();
        }
    }
    getDefaultTextStyle(orientation) {
        let portrait = orientation
            ? orientation === OrientationType.PORTRAIT
            : this.renderer.isPortrait();
        return portrait
            ? this.portraitDefaultTextStyle
            : this.landscapeDefaultTextStyle;
    }
    crop(resizeFont = false) {
        if (!this.uiText) {
            this.initialiseUiText();
        }
        if (this.renderer.isResizing()) {
            return;
        }
        if (resizeFont) {
            this.resizeFont();
        }
        this.refreshBounds(this.uiText.width, this.uiText.height);
        this.updateBounds();
    }
    refreshBounds(width, height) {
        const size = this.getSize();
        const ww = width || size.x;
        const hh = height || size.y;
        this.drawBounds(ww, hh, true);
    }
    /**
     * Applies a new font style.
     * @param fontProperties - New style.
     */
    setStyle(fontProperties) {
        this.uiText.style = this.createTextStyle(this.uiProperties, fontProperties);
        this._landscapeDirty = true;
        this._portraitDirty = true;
        this.updateAlign();
    }
    /**
     * Find the lang value, then sets the text.
     * @param key - Lang key
     * @param refreshStyle - Will the text be repositioned and resized to fit within its bounds. Default `false`.
     */
    setLangKey(key, refreshStyle = false) {
        this.setText(this.langManager.getLang(key), refreshStyle);
    }
    /**
     * Join multiple lang values together with "\n" in between them.
     * Then sets the text.
     */
    setLangKeys(keys, refreshStyle = false) {
        let str = "";
        let i = 0;
        for (i = 0; i < keys.length - 1; i++) {
            str += this.langManager.getLang(keys[i]) + "\n";
        }
        if (keys.length) {
            str += this.langManager.getLang(keys[i]);
        }
        this.setText(str, refreshStyle);
    }
    getTextBounds() {
        return this.bounds;
    }
    /**
     * @returns The original unformatted text.
     */
    getOriginalText() {
        return this.originalText;
    }
    /**
     * @returns The formatted text.
     */
    getText() {
        return this.formattedText;
    }
    getHolder() {
        return this.holder;
    }
    getProperties() {
        return this.uiProperties;
    }
    getWidth() {
        this.initialiseUiText();
        return this.bounds ? this.bounds.width : this.uiText.width;
    }
    getHeight() {
        this.initialiseUiText();
        return this.bounds ? this.bounds.height : this.uiText.height;
    }
    async setAnchorX(x) {
        if (!this._initialised) {
            await this.initialiseUiText();
        }
        this.uiText && (this.uiText.anchor.x = x);
    }
    async setAnchorY(y) {
        if (!this._initialised) {
            await this.initialiseUiText();
        }
        this.uiText && (this.uiText.anchor.y = y);
    }
    static createField(...args) {
        let _parent, _name, _properties, _portraitProperties;
        if (typeof args[1] === "string") {
            [_parent, _name, _properties, _portraitProperties] = args;
        }
        else {
            [_parent, _properties, _portraitProperties] = args;
            _name = "TextField";
        }
        let field;
        if (_properties) {
            field = new TitanText(_properties, _portraitProperties);
            if (field && _parent) {
                _parent.addChild(field);
            }
        }
        return field;
    }
    /**
     * Creates another TitanText like `this` that's already a child of `this.parent`.
     */
    clone() {
        const clone = TitanText.createField(this.parent, this.landscapeProperties, this.portraitProperties);
        clone.setText(this.getText(), true);
        clone.position.set(this.position.x, this.position.y);
        clone.scale.set(this.scale.x, this.scale.y);
        clone.rotation = this.rotation;
        clone.alpha = this.alpha;
        clone.visible = this.visible;
        return clone;
    }
    /**
     * @deprecated Refresh your text style with `field.setText(field.getText(), true)` instead.
     */
    _refresh() {
        this.initialiseUiText();
        if (this.renderer.isPortrait() && this.hasPortraitProperties) {
            this.landscapeTextStyle = this.uiText.style.clone();
            this.uiText.style = this._portraitDirty
                ? this.portraitDefaultTextStyle.clone()
                : this.portraitTextStyle;
        }
        else {
            this.portraitTextStyle = this.uiText.style.clone();
            this.uiText.style = this._landscapeDirty
                ? this.landscapeDefaultTextStyle.clone()
                : this.landscapeTextStyle;
        }
        window.requestAnimationFrame(() => {
            if (this.renderer) {
                //edge case where the field gets destroyed before the next anim frame
                this.updateAlign();
            }
        });
    }
    createProperties(landscapeProperties, portraitProperties) {
        Renderer.textFieldResolution =
            Renderer.textFieldResolution ||
                Math.min(2, this.renderer.getResolution());
        super.createProperties(landscapeProperties, portraitProperties);
        this.landscapeProperties = landscapeProperties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    createLocale() {
        this.isLocalise = StringUtil.isTrue(WebUtil.getUrlParameter(ControlConstants.LOCALISE));
        this.isGameDev = StringUtil.isTrue(WebUtil.getUrlParameter(ControlConstants.GAME_DEV));
        this.locale = this.langManager.getLocale();
    }
    createHolder() {
        this.holder = new PIXI.Container();
        this.addChild(this.holder);
        this.holder.renderable = false;
    }
    createBounds() {
        let size = this.getSize();
        if (size) {
            this.drawBounds(size.x, size.y, size instanceof PIXI.Point);
            this.updateBounds();
        }
        else {
            if (this.isLocalise) {
                this.setSize(100, 50);
                size = this.getSize();
            }
            this.holder.renderable = true;
        }
        if (this.isLocalise) {
            LayoutUtil.enableDrag(this);
        }
    }
    checkBounds(width, height, respectDirty = true) {
        try {
            const metricsData = this.getMetricsData(respectDirty);
            const xFit = width <= 0 || metricsData.width <= width;
            const yFit = height <= 0 ||
                metricsData.height <=
                    Math.max(this.uiText.style.lineHeight +
                        this.uiText.style.dropShadowDistance, height);
            return xFit && yFit;
        }
        catch (error) {
            Logger.warn(error);
            // return true;
        }
    }
    setSize(width, height) {
        //really only useful for debugging
        if (arguments.length === 2) {
            this.uiProperties.size = new PIXI.Point(width, height);
        }
        else {
            this.uiProperties.size = null;
        }
        this.createBounds();
    }
    updateHorizontalAlign() {
        switch (this.uiProperties.horizontalAlign) {
            case ControlConstants.CENTER:
                this.setAnchorX(0.5);
                break;
            case ControlConstants.RIGHT:
                this.setAnchorX(1);
                break;
            case ControlConstants.LEFT:
            default:
                this.setAnchorX(0);
                break;
        }
    }
    /**
     * Change `this.holder.y` separately from the position,
     * makes calculating the true center difficult.
     */
    updateVerticalAlign(size) {
        switch (this.uiProperties.verticalAlign) {
            case ControlConstants.CENTER:
                this.setAnchorY(0.5);
                this.holder.y = size.y / 2;
                break;
            case ControlConstants.BOTTOM:
                this.setAnchorY(1);
                this.holder.y = size.y;
                break;
            case ControlConstants.TOP:
            default:
                this.setAnchorY(0);
                this.holder.y = 0;
                break;
        }
    }
    resizeFont() {
        const size = this.uiText.style.fontSize;
        const lines = this.uiProperties._metrics.lines.length;
        const targetSize = this.getSize();
        let newSize = size;
        while (newSize >= 1) {
            const newStyle = new PIXI.TextStyle({ ...this.uiText.style, fontSize: newSize });
            const metricsData = PIXI.TextMetrics.measureText(this.getText(), newStyle, this.uiText.style.wordWrap);
            if (metricsData.height <= targetSize.y && metricsData.width <= targetSize.x) {
                break;
            }
            const difference = metricsData.height - targetSize.y;
            if (difference > this.uiProperties._metrics.lineHeight) {
                newSize = newSize - Math.max(1, newSize / 4);
            }
            else if (difference > 10 * lines) {
                newSize = newSize - Math.max(1, newSize / 15);
            }
            else {
                newSize = newSize - 1;
            }
            Logger.verbose(`${this.getText()} Resizing font from ${size} to ${newSize}`);
            this.uiProperties._metrics = metricsData; //update metricsData cache
        }
        this.uiProperties._resizes += 1;
        let paddingPercentage = this.uiProperties.fontProperties.font.paddingPercentage;
        this.uiText.style["fontSize"] = Math.max(1, Math.round(newSize));
        this.uiText.style["padding"] = Math.floor(newSize * paddingPercentage);
        Logger.debug(`${this.getText()} Re-rendering font from ${size} to ${newSize}`);
    }
    createTextFields() {
        this._initialised = false;
        this._allowInitialise = true;
        this._portraitDirty = true;
        this._landscapeDirty = true;
        if (this.uiProperties) {
            this.landscapeDefaultTextStyle = this.createTextStyle(this.landscapeProperties, this.landscapeProperties.fontProperties);
            this.portraitDefaultTextStyle = this.hasPortraitProperties
                ? this.createTextStyle(this.portraitProperties, this.portraitProperties.fontProperties)
                : this.landscapeDefaultTextStyle;
            if (this.uiProperties.visible) {
                this.uiText = this.createPixiText(this.getDefaultTextStyle().clone());
            }
            if (this.uiProperties.langKey) {
                this.setLangKey(this.uiProperties.langKey);
            }
            else {
                this.formattedText = "";
            }
        }
    }
    createTextStyle(properties = this.uiProperties, fontProperties) {
        const style = TitanText.makeTextStyle(properties, fontProperties);
        if (this.uiProperties.multiline) {
            style["breakWords"] = this.langManager.isBreakWords();
        }
        return new PIXI.TextStyle(style);
    }
    createPixiText(style) {
        const pixiText = new PIXI.Text("", style);
        pixiText.resolution = Renderer.textFieldResolution;
        this.holder.addChild(pixiText);
        return pixiText;
    }
    createBackground() {
        if (this.uiProperties.backgroundProperties) {
            this.background = TitanSprite.createSprite(this.holder, "background", this.landscapeProperties.backgroundProperties, this.portraitProperties.backgroundProperties);
            this.background.getPixiSprite().mask = this.uiText;
        }
    }
    updateTextField(value) {
        this.originalText = value;
        if (!this.locale.isIgnoreCase()) {
            let font = this.uiProperties.fontProperties;
            if (font.uppercase) {
                value = value.toUpperCase();
            }
            else if (font.lowercase) {
                value = value.toLowerCase();
            }
        }
        this.formattedText = value;
        if (this.uiText) {
            this.uiText.text = this.formattedText;
        }
        this.updateAlign();
    }
    updateAlign() {
        this.bounds ? this.updateBounds() : this.updateNoBounds();
    }
    updateRotation() {
        if (this.uiProperties.rotation != 0) {
            this.rotation = GraphicsUtil.toRadians(this.uiProperties.rotation);
        }
    }
    handleRotate(event) {
        const data = event?.getCurrentTarget() || {};
        this.uiProperties = (this.getCurrentProperties(data.forceOrientation));
        super.handleRotate(event);
        if (this.uiText) {
            if (this.hasPortraitProperties) {
                if (this.renderer.isPortrait()) {
                    this.landscapeTextStyle = this.uiText.style.clone();
                    this.uiText.style = this._portraitDirty
                        ? this.portraitDefaultTextStyle.clone()
                        : this.portraitTextStyle;
                }
                else {
                    this.portraitTextStyle = this.uiText.style.clone();
                    this.uiText.style = this._landscapeDirty
                        ? this.landscapeDefaultTextStyle.clone()
                        : this.landscapeTextStyle;
                }
            }
        }
    }
    handleDeinitialiseText(event) {
        if (this.uiText) {
            this.deinitialiseUiText();
            this._allowInitialise = false;
        }
    }
    async handleReinitialiseText(event) {
        if (!this._allowInitialise) {
            this._allowInitialise = true;
            await this.initialiseUiText();
        }
        if (this.uiText && this.renderer) {
            this.updateAlign();
        }
    }
    handleResize(event) {
        super.handleResize(event);
    }
    isShowing(node) {
        return (node.parent &&
            (node.parent === this.renderer.getStage() ||
                this.isShowing(node.parent)));
    }
}
//RM: Temp default constants. These should probably be in a property store somewhere.
TitanText.defaultFontName = "sans-serif";
TitanText.defaultFontSize = 20;
TitanText.defaultFontColor = 0x7f9282; //grey
//# sourceMappingURL=TitanText.js.map